import { useEffect, useRef, useState } from "react";
import { SearchIcon, Calender } from "../../assets/Icons";

import { UserTableProps, UserSearchFilterProps } from "./types";
import { formatMetricValue } from "../../components/userManagement/userDetailModal";

const formatDate = (timestamp?: string) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusStyles = (status?: string) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (["active", "verified", "enabled"].includes(normalizedStatus)) {
    return "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300";
  }

  if (["pending", "processing"].includes(normalizedStatus)) {
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300";
  }

  if (
    ["inactive", "disabled", "banned", "suspended"].includes(normalizedStatus)
  ) {
    return "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300";
  }

  return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300";
};

const StatusBadge = ({ status }: { status?: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${getStatusStyles(
      status,
    )}`}
  >
    {status || "Unknown"}
  </span>
);

export const UsersTable = ({
  users,
  onUserClick,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  total,
  perPage = 15,
}: UserTableProps) => {
  const page = currentPage || 1;
  const pages = totalPages || 1;
  const startItem = total === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total || users.length);

  const goToNextPage = () => {
    if (onPageChange && page < pages) {
      onPageChange(page + 1);
    }
  };

  const goToPreviousPage = () => {
    if (onPageChange && page > 1) {
      onPageChange(page - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-[#FFFFFF1A] rounded-2xl">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            No users found
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Try adjusting your search filters to see more results
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left hidden sm:table-cell">Email</th>
              <th className="px-6 text-left sm:hidden">User</th>
              <th className="px-6 text-left">Username</th>
              <th className="px-6 text-left hidden md:table-cell">Name</th>
              <th className="px-6 text-left">DG Coin Balance</th>
              <th className="px-6 text-left">DG Point Balance</th>
              <th className="px-6 text-left hidden lg:table-cell">Country</th>
              <th className="px-6 text-left hidden sm:table-cell">
                Date Joined
              </th>
              <th className="px-6 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {users.map((user) => (
              <tr
                key={user.user_id}
                className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-gray-50/5 cursor-pointer"
                onClick={() => onUserClick(user)}
              >
                <td className="px-6 hidden sm:table-cell">
                  <div className="flex flex-col">
                    <span>{user.email || "N/A"}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {user.phone_number || "No phone"}
                    </span>
                  </div>
                </td>
                <td className="px-6 sm:hidden">
                  <div className="flex flex-col">
                    <span>{user.username || "N/A"}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {user.email || "N/A"}
                    </span>
                  </div>
                </td>
                <td className="px-6">{user.username || "N/A"}</td>
                <td className="px-6 hidden md:table-cell">
                  {`${user.firstname || ""} ${user.lastname || ""}`.trim() ||
                    "N/A"}
                </td>
                <td className="px-6">
                  {formatMetricValue(
                    user?.dg_coin_wallet_balance || user?.dgcoinwallet?.balance,
                  )}
                </td>
                <td className="px-6">
                  {formatMetricValue(
                    user?.dg_point_wallet_balance ||
                      user?.dgpointwallet?.balance,
                  )}
                </td>
                <td className="px-6 hidden lg:table-cell">
                  {user.country || "N/A"}
                </td>
                <td className="px-6 hidden sm:table-cell">
                  <div className="flex flex-col">
                    <span>{formatDate(user.created_at)}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {formatTime(user.created_at)}
                    </span>
                  </div>
                </td>
                <td className="px-6">
                  <StatusBadge status={user.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center border-t border-[#FFFFFF1A] p-4 gap-2">
        <div className="flex gap-2">
          <button
            className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
            onClick={goToPreviousPage}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <button
            className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
            onClick={goToNextPage}
            disabled={page >= pages || loading}
          >
            Next
          </button>
        </div>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Showing {startItem} to {endItem} of {total || users.length} results
          {pages > 1 && ` (Page ${page} of ${pages})`}
        </span>
      </div>
    </div>
  );
};

export const UserSearchFilter: React.FC<UserSearchFilterProps> = ({
  onFilterChange,
  onClearFilters,
  filters,
  filterOptions,
  countries,
  loading,
  onExport,
  exporting = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");
  const isMounted = useRef(false);

  useEffect(() => {
    setSearchTerm(filters?.search || "");
  }, [filters?.search]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const handler = setTimeout(() => {
      if (searchTerm !== (filters?.search || "")) {
        onFilterChange("search", searchTerm);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, onFilterChange, filters?.search]);

  return (
    <div className="w-full flex flex-wrap items-center justify-between gap-3 mb-4">
      {/* Search Input Box */}
      <div className="relative w-full md:flex-1 md:min-w-[240px] xl:max-w-[400px]">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search for a user..."
          className="w-full h-[40px] p-2 pl-10 border rounded-3xl text-sm dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A] outline-none disabled:opacity-50"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>

      {/* Control Inputs Action Row */}
      <div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:flex-initial">
        {/* Start Date */}
        <div className="relative flex-1 min-w-[125px] sm:flex-initial sm:w-[145px]">
          <input
            type="date"
            value={filters?.created_from || ""}
            onChange={(e) => onFilterChange("created_from", e.target.value)}
            disabled={loading}
            className="w-full h-[40px] p-2 pl-9 bg-[#F4F4F5] rounded-3xl text-xs dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A] outline-none disabled:opacity-50"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Calender />
          </div>
        </div>

        {/* End Date */}
        <div className="relative flex-1 min-w-[125px] sm:flex-initial sm:w-[145px]">
          <input
            type="date"
            value={filters?.created_to || ""}
            onChange={(e) => onFilterChange("created_to", e.target.value)}
            disabled={loading}
            className="w-full h-[40px] p-2 pl-9 bg-[#F4F4F5] rounded-3xl text-xs dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A] outline-none disabled:opacity-50"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Calender />
          </div>
        </div>

        {/* Country Select Dropdown */}
        <div className="flex-1 min-w-[110px] sm:flex-initial sm:w-[120px]">
          <select
            name="country"
            value={filters?.country || ""}
            onChange={(e) => onFilterChange("country", e.target.value)}
            disabled={loading}
            className="w-full h-[40px] p-2 bg-[#F4F4F5] rounded-3xl text-xs dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] outline-none cursor-pointer disabled:opacity-50"
          >
            <option value="" className="dark:bg-zinc-900 dark:text-white">
              Country
            </option>
            {countries.map((country) => (
              <option
                key={country.id}
                value={country.name}
                className="dark:bg-zinc-900 dark:text-white"
              >
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {/* Status Select Dropdown */}
        <div className="flex-1 min-w-[110px] sm:flex-initial sm:w-[130px]">
          <select
            name="status"
            value={filters?.status || ""}
            onChange={(e) => onFilterChange("status", e.target.value)}
            disabled={loading}
            className="w-full h-[40px] p-2 bg-[#F4F4F5] rounded-3xl text-xs dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] outline-none cursor-pointer disabled:opacity-50"
          >
            <option value="" className="dark:bg-zinc-900 dark:text-white">
              All Status
            </option>
            {filterOptions?.status_options?.map((status: string) => (
              <option
                key={status}
                value={status}
                className="dark:bg-zinc-900 dark:text-white"
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Button */}
        {/* CHANGED: Standardized height matching and added whitespace preservation rules */}
        <button
          type="button"
          onClick={onClearFilters}
          disabled={loading || exporting}
          className="h-[40px] px-4 rounded-3xl text-xs font-medium border border-[#D0D5DD] dark:border-[#FFFFFF1A] dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33] disabled:opacity-50 whitespace-nowrap shrink-0 flex-1 sm:flex-initial"
        >
          Clear
        </button>
        {onExport && (
          <button
            type="button"
            onClick={onExport}
            disabled={loading || exporting}
            className="h-[40px] px-4 rounded-3xl text-xs font-medium text-white bg-gradient-to-r from-[#027EC2] to-[#039EF2] hover:opacity-90 disabled:opacity-50 whitespace-nowrap shrink-0 flex-1 sm:flex-initial"
          >
            {exporting ? "Preparing CSV..." : "Download CSV"}
          </button>
        )}
      </div>
    </div>
  );
};
